/*
  File: src/pages/DatasetDetail.jsx
  Purpose: Dataset detail page with images, labels, and annotations management
  Features:
  - View dataset overview
  - List and manage images
  - Create and manage labels
  - Annotate images with bounding boxes
  - Delete annotations
*/
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import ImageAnnotationModal from '../components/dataset/modals/ImageAnnotationModal';

function DatasetDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [dataset, setDataset] = useState(null);
    const [images, setImages] = useState([]);
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [paginationMeta, setPaginationMeta] = useState({
        hasNext: false,
        hasPrevious: false,
        totalPages: 0
    });
    const pageSize = 10;
    
    // Image annotation states
    const [selectedImage, setSelectedImage] = useState(null);
    const [annotations, setAnnotations] = useState([]);
    const [isAnnotating, setIsAnnotating] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState('');
    const [isDrawing, setIsDrawing] = useState(false);
    const [startPoint, setStartPoint] = useState(null);
    const [currentBox, setCurrentBox] = useState(null);
    const canvasRef = useRef(null);
    const imageRef = useRef(null);

    // Modal states
    const [showLabelModal, setShowLabelModal] = useState(false);
    const [newLabelName, setNewLabelName] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [uploadMode, setUploadMode] = useState('file'); // 'file' or 's3'
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [labelToDelete, setLabelToDelete] = useState(null);
    const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [newImageData, setNewImageData] = useState({
        s3_bucket: '',
        s3_key: '',
        width_px: '',
        height_px: ''
    });
    
    // Autolabel state
    const [autolabelLoading, setAutolabelLoading] = useState({});
    const [pendingAutolabels, setPendingAutolabels] = useState({}); // Store pending autolabel results
    const [selectedImages, setSelectedImages] = useState(new Set()); // Selected images for bulk operations
    const [showBulkAutolabelModal, setShowBulkAutolabelModal] = useState(false);
    const [bulkAutolabelSettings, setBulkAutolabelSettings] = useState({
        type: 'series',
        minConfidence: 80
    });
    const [currentImageAutolabelSettings, setCurrentImageAutolabelSettings] = useState({
        type: 'series',
        minConfidence: 80
    });
    const [clearing, setClearing] = useState(false);
    const [showClearDatasetModal, setShowClearDatasetModal] = useState(false);
    const [showDeleteSelectedModal, setShowDeleteSelectedModal] = useState(false);
    const [deletingSelected, setDeletingSelected] = useState(false);
    
    // Relabel annotation state
    const [showRelabelModal, setShowRelabelModal] = useState(false);
    const [annotationToRelabel, setAnnotationToRelabel] = useState(null);
    const [relabelTargetLabelId, setRelabelTargetLabelId] = useState('');
    const [relabeling, setRelabeling] = useState(false);

    const rawBase = (process.env.REACT_APP_API_BASE || '').trim();
    const cleanedBase = rawBase.replace(/^@+/, '');
    const API_BASE = cleanedBase || '/dev';

    // Per-image annotations for grid overlay: { [imageId]: Annotation[] }
    const [imageAnnotationsMap, setImageAnnotationsMap] = useState({});
    const [imageFilter, setImageFilter] = useState('all'); // 'all' | 'annotated' | 'unannotated'
    // Server-side label filter (uses backend ?label_id)
    const [selectedLabelFilterId, setSelectedLabelFilterId] = useState(null);
    // With server-side filtering, just use images as-is
    const filteredImages = React.useMemo(() => {
        return Array.isArray(images) ? images : [];
    }, [images]);
    const selectedVisibleCount = React.useMemo(() => {
        return filteredImages.reduce((acc, img) => acc + (selectedImages.has(img.id) ? 1 : 0), 0);
    }, [filteredImages, selectedImages]);

    // Fetch dataset details
    const fetchDataset = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/rekog/datasets/${id}`, {
                headers: { accept: 'application/json' },
                credentials: 'include'
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setDataset(data);
        } catch (e) {
            setError('Failed to load dataset');
        }
    }, [API_BASE, id]);

    // Fetch images with pagination
    const fetchImages = useCallback(async (page = 1) => {
        setImagesLoading(true);
        try {
            const labelQuery = selectedLabelFilterId ? `&label_id=${encodeURIComponent(selectedLabelFilterId)}` : '';
            const annotatedQuery = imageFilter === 'annotated' ? '&annotated=true' : (imageFilter === 'unannotated' ? '&annotated=false' : '');
            const res = await fetch(`${API_BASE}/rekog/datasets/${id}/images?page=${page}&page_size=${pageSize}${labelQuery}${annotatedQuery}`, {
                headers: { accept: 'application/json' },
                credentials: 'include'
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            
            // Handle both new paginated response and legacy array response
            if (data && typeof data === 'object' && data.items && data.pagination) {
                // New paginated response
                setImages(Array.isArray(data.items) ? data.items : []);
                setTotalCount(data.pagination.total_count);
                setPaginationMeta({
                    hasNext: data.pagination.has_next,
                    hasPrevious: data.pagination.has_previous,
                    totalPages: data.pagination.total_pages
                });
            } else if (Array.isArray(data)) {
                // Legacy array response - fallback
                setImages(data);
                setTotalCount(data.length);
                setPaginationMeta({
                    hasNext: false,
                    hasPrevious: false,
                    totalPages: 1
                });
            } else {
                setImages([]);
                setTotalCount(0);
                setPaginationMeta({
                    hasNext: false,
                    hasPrevious: false,
                    totalPages: 0
                });
            }
        } catch (e) {
            setError('Failed to load images');
            setImages([]);
            setTotalCount(0);
        } finally {
            setImagesLoading(false);
        }
    }, [API_BASE, id, pageSize, selectedLabelFilterId, imageFilter]);

    // Fetch labels
    const fetchLabels = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/rekog/datasets/${id}/labels`, {
                headers: { accept: 'application/json' },
                credentials: 'include'
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setLabels(Array.isArray(data) ? data : []);
        } catch (e) {
            setError('Failed to load labels');
        }
    }, [API_BASE, id]);

    // Fetch annotations for selected image
    const fetchAnnotations = useCallback(async (imageId) => {
        try {
            const res = await fetch(`${API_BASE}/rekog/images/${imageId}/annotations`, {
                headers: { accept: 'application/json' },
                credentials: 'include'
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setAnnotations(Array.isArray(data) ? data : []);
            // Keep grid overlay in sync for this image
            setImageAnnotationsMap(prev => ({ ...prev, [imageId]: Array.isArray(data) ? data : [] }));
        } catch (e) {
            setError('Failed to load annotations');
        }
    }, [API_BASE]);

    // Navigation between images (respects current filter order)
    const currentIndex = React.useMemo(() => {
        if (!selectedImage) return -1;
        const list = (typeof filteredImages !== 'undefined') ? filteredImages : images;
        return (list || []).findIndex(img => img.id === selectedImage.id);
    }, [selectedImage, filteredImages, images]);

    const gotoImageAt = async (idx) => {
        const list = (typeof filteredImages !== 'undefined') ? filteredImages : images;
        if (!Array.isArray(list) || idx < 0 || idx >= list.length) return;
        const img = list[idx];
        setSelectedImage(img);
        await fetchAnnotations(img.id);
    };

    const gotoPrevImage = async () => {
        if (currentIndex > 0) {
            await gotoImageAt(currentIndex - 1);
        }
    };

    const gotoNextImage = async () => {
        const list = (typeof filteredImages !== 'undefined') ? filteredImages : images;
        if (currentIndex >= 0 && currentIndex < (list.length - 1)) {
            await gotoImageAt(currentIndex + 1);
        }
    };

    // Load annotations for all images to render overlays in grid
    useEffect(() => {
        let cancelled = false;
        const loadAll = async () => {
            const nextMap = {};
            await Promise.all((images || []).map(async (img) => {
                try {
                    const res = await fetch(`${API_BASE}/rekog/images/${img.id}/annotations`, {
                        headers: { accept: 'application/json' },
                        credentials: 'include'
                    });
                    if (res.ok) {
                        const arr = await res.json();
                        nextMap[img.id] = Array.isArray(arr) ? arr : [];
                    } else {
                        nextMap[img.id] = [];
                    }
                } catch {
                    nextMap[img.id] = [];
                }
            }));
            if (!cancelled) setImageAnnotationsMap(nextMap);
        };
        if (images && images.length > 0) {
            loadAll();
        } else {
            setImageAnnotationsMap({});
        }
        return () => { cancelled = true; };
    }, [images, API_BASE]);

    // Create new label
    const createLabel = async () => {
        if (!newLabelName.trim()) return;
        
        try {
            const res = await fetch(`${API_BASE}/rekog/datasets/${id}/labels`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ name: newLabelName.trim() })
            });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            await fetchLabels();
            await fetchDataset(); // Refresh counts
            setShowLabelModal(false);
            setNewLabelName('');
        } catch (e) {
            setError(e.message || 'Failed to create label');
        }
    };

    // Show delete confirmation modal
    const showDeleteConfirmation = (label) => {
        setLabelToDelete(label);
        setShowDeleteModal(true);
    };

    // Delete label
    const deleteLabel = async () => {
        if (!labelToDelete) return;

        try {
            const res = await fetch(`${API_BASE}/rekog/labels/${labelToDelete.id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            await fetchLabels();
            await fetchDataset(); // Refresh counts
            // Refresh images to update annotation counts
            await fetchImages();
            // If we're viewing annotations and they used this label, refresh them
            if (selectedImage) {
                await fetchAnnotations(selectedImage.id);
            }
            
            setShowDeleteModal(false);
            setLabelToDelete(null);
        } catch (e) {
            setError(e.message || 'Failed to delete label');
        }
    };

    // Show delete image confirmation modal
    const showDeleteImageConfirmation = (image) => {
        setImageToDelete(image);
        setShowDeleteImageModal(true);
    };

    // Delete image
    const deleteImage = async () => {
        if (!imageToDelete) return;

        try {
            const res = await fetch(`${API_BASE}/rekog/images/${imageToDelete.id}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            await fetchImages();
            await fetchDataset(); // Refresh counts
            await fetchLabels(); // Refresh per-label annotation counts
            
            // If we're currently viewing this image, close the annotation modal
            if (selectedImage && selectedImage.id === imageToDelete.id) {
                setSelectedImage(null);
                setAnnotations([]);
            }
            
            setShowDeleteImageModal(false);
            setImageToDelete(null);
        } catch (e) {
            setError(e.message || 'Failed to delete image');
        }
    };

    // Clear entire dataset (images, labels, annotations) using backend endpoint
    const clearDataset = async () => {
        if (!id) return;
        setClearing(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/rekog/datasets/${id}/images/clear`, {
                method: 'DELETE',
                headers: { accept: 'application/json' },
                credentials: 'include'
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || `HTTP ${res.status}`);
            }
            setShowClearDatasetModal(false);
            setSelectedImage(null);
            setAnnotations([]);
            setSelectedImages(new Set());
            setPendingAutolabels({});
            await Promise.all([
                fetchDataset(),
                fetchImages(),
                fetchLabels()
            ]);
        } catch (e) {
            setError(e.message || 'Failed to clear dataset');
        } finally {
            setClearing(false);
        }
    };

    // Delete only the selected images
    const deleteSelectedImages = async () => {
        if (selectedImages.size === 0) return;
        setDeletingSelected(true);
        setError('');
        try {
            const ids = Array.from(selectedImages);
            const results = await Promise.all(ids.map(async (imgId) => {
                try {
                    const res = await fetch(`${API_BASE}/rekog/images/${imgId}`, {
                        method: 'DELETE',
                        credentials: 'include'
                    });
                    return { id: imgId, ok: res.ok, status: res.status };
                } catch (e) {
                    return { id: imgId, ok: false, status: 0 };
                }
            }));
            const failed = results.filter(r => !r.ok);
            if (failed.length > 0) {
                setError(`Failed to delete ${failed.length} of ${results.length} selected images.`);
            }
            setShowDeleteSelectedModal(false);
            // Clean up selected set by removing those successfully deleted
            const deletedSet = new Set(results.filter(r => r.ok).map(r => r.id));
            setSelectedImages(prev => new Set(Array.from(prev).filter(id => !deletedSet.has(id))));
            // Refresh data
            await Promise.all([
                fetchDataset(),
                fetchImages(),
                fetchLabels()
            ]);
        } catch (e) {
            setError(e.message || 'Failed to delete selected images');
        } finally {
            setDeletingSelected(false);
        }
    };

    // Add image (file upload or S3 reference)
    const addImage = async () => {
        if (uploadMode === 'file' && !selectedFile) {
            setError('Please select a file to upload');
            return;
        }
        if (uploadMode === 's3' && (!newImageData.s3_bucket.trim() || !newImageData.s3_key.trim() || !newImageData.width_px || !newImageData.height_px)) {
            setError('All S3 fields are required');
            return;
        }

        setUploading(true);
        try {
            let res;
            
            if (uploadMode === 'file') {
                // File upload
                const formData = new FormData();
                formData.append('file', selectedFile);
                
                // Add dimensions if provided
                if (newImageData.width_px) formData.append('width_px', newImageData.width_px);
                if (newImageData.height_px) formData.append('height_px', newImageData.height_px);
                
                res = await fetch(`${API_BASE}/rekog/datasets/${id}/images`, {
                    method: 'POST',
                    credentials: 'include',
                    body: formData
                });
            } else {
                // S3 reference
                res = await fetch(`${API_BASE}/rekog/datasets/${id}/images`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        accept: 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        s3_bucket: newImageData.s3_bucket.trim(),
                        s3_key: newImageData.s3_key.trim(),
                        width_px: parseInt(newImageData.width_px),
                        height_px: parseInt(newImageData.height_px)
                    })
                });
            }
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            await fetchImages();
            await fetchDataset(); // Refresh counts
            setShowImageModal(false);
            setSelectedFile(null);
            setNewImageData({
                s3_bucket: '',
                s3_key: '',
                width_px: '',
                height_px: ''
            });
        } catch (e) {
            setError(e.message || 'Failed to add image');
        } finally {
            setUploading(false);
        }
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
        
        // Auto-detect dimensions if it's an image
        if (file && file.type.startsWith('image/')) {
            const img = new Image();
            img.onload = () => {
                setNewImageData(prev => ({
                    ...prev,
                    width_px: img.naturalWidth.toString(),
                    height_px: img.naturalHeight.toString()
                }));
            };
            img.src = URL.createObjectURL(file);
        }
    };

    // Create annotation
    const createAnnotation = async (imageId, labelId, left, top, width, height) => {
        try {
            const res = await fetch(`${API_BASE}/rekog/images/${imageId}/annotations`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    label_id: labelId,
                    left_px: Math.round(left),
                    top_px: Math.round(top),
                    width_px: Math.round(width),
                    height_px: Math.round(height)
                })
            });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            await fetchAnnotations(imageId);
            await fetchDataset(); // Refresh overall counts
            await fetchLabels(); // Refresh per-label annotation counts
        } catch (e) {
            setError(e.message || 'Failed to create annotation');
        }
    };

    // Delete annotation
    const deleteAnnotation = async (annotationId) => {
        try {
            const res = await fetch(`${API_BASE}/rekog/annotations/${annotationId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            
            await fetchAnnotations(selectedImage.id);
            await fetchDataset(); // Refresh counts
            await fetchLabels(); // Refresh per-label annotation counts
        } catch (e) {
            setError('Failed to delete annotation');
        }
    };

    // Relabel annotation - open modal for label selection
    const relabelAnnotation = (annotation) => {
        setAnnotationToRelabel(annotation);
        setRelabelTargetLabelId('');
        setShowRelabelModal(true);
    };

    // Execute relabel - call backend API
    const executeRelabel = async () => {
        if (!annotationToRelabel || !relabelTargetLabelId) return;
        
        setRelabeling(true);
        setError('');
        
        try {
            const res = await fetch(`${API_BASE}/rekog/annotations/${annotationToRelabel.id}/relabel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ label_id: parseInt(relabelTargetLabelId) })
            });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            // Close modal and refresh data
            setShowRelabelModal(false);
            setAnnotationToRelabel(null);
            setRelabelTargetLabelId('');
            
            await fetchAnnotations(selectedImage.id);
            await fetchDataset(); // Refresh counts
            await fetchLabels(); // Refresh per-label annotation counts
        } catch (e) {
            setError(e.message || 'Failed to relabel annotation');
        } finally {
            setRelabeling(false);
        }
    };

    // Autolabel function - just generates labels, doesn't save them
    const autolabelImage = async (image, settings = null) => {
        setAutolabelLoading(prev => ({ ...prev, [image.id]: true }));
        setError('');
        
        // Use provided settings or current image settings
        const { type, minConfidence } = settings || currentImageAutolabelSettings;
        
        try {
            const res = await fetch(`${API_BASE}/rekog/autolabel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    s3_bucket: image.s3_bucket,
                    s3_key: image.s3_key,
                    type: type,
                    min_confidence: minConfidence
                })
            });
            
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${res.status}`);
            }
            
            const autoLabelData = await res.json();
            
            // Process detected objects and prepare annotations
            const pendingAnnotations = [];
            
            for (const detectedLabel of autoLabelData.labels || []) {
                if (!detectedLabel.name || !detectedLabel.box) continue;
                
                // Convert relative bounding box to pixel coordinates
                const box = detectedLabel.box;
                const left_px = Math.round(box.Left * image.width_px);
                const top_px = Math.round(box.Top * image.height_px);
                const width_px = Math.round(box.Width * image.width_px);
                const height_px = Math.round(box.Height * image.height_px);
                
                pendingAnnotations.push({
                    labelName: detectedLabel.name,
                    confidence: detectedLabel.confidence,
                    left_px,
                    top_px,
                    width_px,
                    height_px
                });
            }
            
            // Store pending results
            setPendingAutolabels(prev => ({
                ...prev,
                [image.id]: {
                    annotations: pendingAnnotations,
                    settings: { type, minConfidence },
                    timestamp: Date.now()
                }
            }));
            
            setError(`✅ Auto-labeling completed! Found ${pendingAnnotations.length} objects. Click "Save Auto-Labels" to add them to the dataset.`);
            
        } catch (e) {
            setError(`Auto-labeling failed: ${e.message}`);
        } finally {
            setAutolabelLoading(prev => ({ ...prev, [image.id]: false }));
        }
    };

    // Save pending autolabels to the dataset
    const saveAutolabels = async (imageId) => {
        const pending = pendingAutolabels[imageId];
        if (!pending) return;
        
        setAutolabelLoading(prev => ({ ...prev, [imageId]: true }));
        setError('');
        
        try {
            let annotationsCreated = 0;
            
            for (const annotation of pending.annotations) {
                // Ensure label exists in dataset
                let label = labels.find(l => l.name === annotation.labelName);
                if (!label) {
                    // Create new label
                    try {
                        const labelRes = await fetch(`${API_BASE}/rekog/datasets/${id}/labels`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                accept: 'application/json'
                            },
                            credentials: 'include',
                            body: JSON.stringify({ name: annotation.labelName })
                        });
                        
                        if (labelRes.ok) {
                            const newLabel = await labelRes.json();
                            label = newLabel;
                            // Update labels list
                            setLabels(prev => [...prev, newLabel]);
                        } else {
                            console.warn(`Failed to create label: ${annotation.labelName}`);
                            continue;
                        }
                    } catch (e) {
                        console.warn(`Error creating label ${annotation.labelName}:`, e);
                        continue;
                    }
                }
                
                // Create annotation
                try {
                    await createAnnotation(imageId, label.id, annotation.left_px, annotation.top_px, annotation.width_px, annotation.height_px);
                    annotationsCreated++;
                } catch (e) {
                    console.warn(`Failed to create annotation for ${annotation.labelName}:`, e);
                }
            }
            
            // Clear pending autolabels for this image
            setPendingAutolabels(prev => {
                const newPending = { ...prev };
                delete newPending[imageId];
                return newPending;
            });
            
            // Refresh data
            await Promise.all([
                fetchDataset(),
                fetchImages(),
                fetchLabels(),
                selectedImage?.id === imageId ? fetchAnnotations(imageId) : Promise.resolve()
            ]);
            
            setError(`✅ Saved ${annotationsCreated} annotations to the dataset!`);
            
        } catch (e) {
            setError(`Failed to save autolabels: ${e.message}`);
        } finally {
            setAutolabelLoading(prev => ({ ...prev, [imageId]: false }));
        }
    };

    // Discard pending autolabels
    const discardAutolabels = (imageId) => {
        setPendingAutolabels(prev => {
            const newPending = { ...prev };
            delete newPending[imageId];
            return newPending;
        });
        setError('Discarded pending auto-labels.');
    };

    // Toggle image selection
    const toggleImageSelection = (imageId) => {
        setSelectedImages(prev => {
            const newSet = new Set(prev);
            if (newSet.has(imageId)) {
                newSet.delete(imageId);
            } else {
                newSet.add(imageId);
            }
            return newSet;
        });
    };

    // Select/deselect all visible images (respecting current filter)
    const toggleAllImages = () => {
        const visible = filteredImages || [];
        const allSelected = visible.length > 0 && visible.every(img => selectedImages.has(img.id));
        setSelectedImages(prev => {
            const next = new Set(prev);
            if (allSelected) {
                visible.forEach(img => next.delete(img.id));
            } else {
                visible.forEach(img => next.add(img.id));
            }
            return next;
        });
    };

    // Bulk autolabel selected images
    const bulkAutolabelSelectedImages = async () => {
        const imagesToProcess = images.filter(img => selectedImages.has(img.id));
        
        for (const image of imagesToProcess) {
            if (!autolabelLoading[image.id]) {
                await autolabelImage(image, bulkAutolabelSettings);
            }
        }
        
        setShowBulkAutolabelModal(false);
        setError(`✅ Bulk auto-labeling completed for ${imagesToProcess.length} images!`);
    };

    // Get annotation counts by label for current image
    const getAnnotationCountsByLabel = () => {
        const counts = {};
        const pendingCounts = {};
        
        // Count existing annotations
        annotations.forEach(annotation => {
            const label = labels.find(l => l.id === annotation.label_id);
            if (label) {
                counts[label.name] = (counts[label.name] || 0) + 1;
            }
        });
        
        // Count pending annotations
        const pending = selectedImage ? pendingAutolabels[selectedImage.id] : null;
        if (pending) {
            pending.annotations.forEach(annotation => {
                pendingCounts[annotation.labelName] = (pendingCounts[annotation.labelName] || 0) + 1;
            });
        }
        
        return { counts, pendingCounts };
    };

    // Canvas drawing handlers
    const getCanvasCoordinates = (e) => {
        const canvas = canvasRef.current;
        const img = imageRef.current;
        if (!canvas || !img) return { x: 0, y: 0 };
        
        const rect = canvas.getBoundingClientRect();
        
        // Get relative coordinates within the canvas element
        const relativeX = (e.clientX - rect.left) / rect.width;
        const relativeY = (e.clientY - rect.top) / rect.height;
        
        // Convert to image pixel coordinates
        return {
            x: relativeX * img.naturalWidth,
            y: relativeY * img.naturalHeight
        };
    };

    const handleMouseDown = (e) => {
        if (!isAnnotating || !selectedLabel) return;
        
        const coords = getCanvasCoordinates(e);
        setIsDrawing(true);
        setStartPoint(coords);
        setCurrentBox({ x: coords.x, y: coords.y, width: 0, height: 0 });
    };

    const handleMouseMove = (e) => {
        if (!isDrawing || !startPoint) return;
        
        const coords = getCanvasCoordinates(e);
        const width = coords.x - startPoint.x;
        const height = coords.y - startPoint.y;
        
        setCurrentBox({
            x: width < 0 ? coords.x : startPoint.x,
            y: height < 0 ? coords.y : startPoint.y,
            width: Math.abs(width),
            height: Math.abs(height)
        });
    };

    const handleMouseUp = async () => {
        if (!isDrawing || !currentBox || !selectedLabel || !selectedImage) return;
        
        // Only create annotation if box has reasonable size
        if (currentBox.width > 10 && currentBox.height > 10) {
            await createAnnotation(
                selectedImage.id,
                parseInt(selectedLabel),
                currentBox.x,
                currentBox.y,
                currentBox.width,
                currentBox.height
            );
            await fetchLabels(); // Refresh per-label annotation counts
        }
        
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentBox(null);
    };

    // Draw annotations on canvas
    const drawAnnotations = useCallback(() => {
        const canvas = canvasRef.current;
        const img = imageRef.current;
        if (!canvas || !img) return;
        
        const ctx = canvas.getContext('2d');
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Calculate scale factors from image pixel coordinates to display coordinates
        const scaleX = img.offsetWidth / img.naturalWidth;
        const scaleY = img.offsetHeight / img.naturalHeight;
        
        // Draw existing annotations (stored in actual image pixel coordinates)
        annotations.forEach((annotation, index) => {
            const label = labels.find(l => l.id === annotation.label_id);
            const color = `hsl(${(index * 60) % 360}, 70%, 50%)`;
            
            // Scale annotation coordinates to display coordinates
            const displayX = annotation.left_px * scaleX;
            const displayY = annotation.top_px * scaleY;
            const displayWidth = annotation.width_px * scaleX;
            const displayHeight = annotation.height_px * scaleY;
            
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.strokeRect(displayX, displayY, displayWidth, displayHeight);
            
            // Draw label
            if (label) {
                ctx.fillStyle = color;
                ctx.font = '12px Arial';
                ctx.fillText(label.name, displayX, displayY - 5);
            }
        });
        
        // Draw pending autolabels (if any)
        const pending = pendingAutolabels[selectedImage?.id];
        if (pending) {
            pending.annotations.forEach((annotation, index) => {
                const displayX = annotation.left_px * scaleX;
                const displayY = annotation.top_px * scaleY;
                const displayWidth = annotation.width_px * scaleX;
                const displayHeight = annotation.height_px * scaleY;
                
                // Draw with dashed blue border for pending
                ctx.strokeStyle = '#3b82f6';
                ctx.lineWidth = 2;
                ctx.setLineDash([8, 4]);
                ctx.strokeRect(displayX, displayY, displayWidth, displayHeight);
                ctx.setLineDash([]);
                
                // Draw label with blue background
                ctx.fillStyle = '#3b82f6';
                ctx.font = '12px Arial';
                ctx.fillText(`${annotation.labelName} (${annotation.confidence.toFixed(1)}%)`, displayX, displayY - 5);
            });
        }
        
        // Draw current drawing box (already in image pixel coordinates)
        if (currentBox) {
            const displayX = currentBox.x * scaleX;
            const displayY = currentBox.y * scaleY;
            const displayWidth = currentBox.width * scaleX;
            const displayHeight = currentBox.height * scaleY;
            
            ctx.strokeStyle = '#ff0000';
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            ctx.strokeRect(displayX, displayY, displayWidth, displayHeight);
            ctx.setLineDash([]);
        }
    }, [annotations, labels, currentBox, pendingAutolabels, selectedImage]);

    // Redraw canvas when annotations or currentBox changes
    useEffect(() => {
        if (selectedImage) {
            drawAnnotations();
        }
    }, [selectedImage, drawAnnotations]);

    // Load data on component mount
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchDataset(), fetchImages(currentPage), fetchLabels()]);
            setLoading(false);
        };
        
        if (id) {
            loadData();
        }
    }, [id, fetchDataset, fetchLabels]);

    // Load images when page changes
    useEffect(() => {
        if (id) {
            fetchImages(currentPage);
        }
    }, [currentPage, fetchImages]);

    // When image filter changes, reset to first page and clear selection
    useEffect(() => {
        setCurrentPage(1);
        setSelectedImages(new Set());
    }, [imageFilter]);

    // Handle clicking a label in the sidebar to filter images
    const handleLabelFilterClick = (labelId) => {
        setSelectedLabelFilterId(prev => {
            const next = prev === labelId ? null : labelId;
            return next;
        });
        // Reset pagination and selection when changing filter
        setCurrentPage(1);
        setSelectedImages(new Set());
    };

    const clearLabelFilter = () => {
        setSelectedLabelFilterId(null);
        setCurrentPage(1);
        setSelectedImages(new Set());
    };

    // Optional: keyboard navigation for prev/next when annotation panel is open
    useEffect(() => {
        const onKey = (e) => {
            if (!selectedImage) return;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                gotoPrevImage();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                gotoNextImage();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedImage, currentIndex, filteredImages, images]);

    // Pagination handlers
    const handlePageChange = (page) => {
        if (page >= 1 && page <= paginationMeta.totalPages) {
            setCurrentPage(page);
        }
    };

    const handleNextPage = () => {
        if (paginationMeta.hasNext) {
            handlePageChange(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (paginationMeta.hasPrevious) {
            handlePageChange(currentPage - 1);
        }
    };

    // Get image URL from the backend response (presigned URL)
    const getImageUrl = (image) => {
        return image.url || `https://${image.s3_bucket}.s3.amazonaws.com/${image.s3_key}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0a0e1a] via-[#1a1f2e] to-[#2a2f3e]">
                <Navbar />
                <main className="pt-16">
                    <div className="flex items-center justify-center py-12">
                        <div className="text-[#4cc3e9] text-sm font-medium flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-[#4cc3e9]/30 border-t-[#4cc3e9] rounded-full animate-spin"></div>
                            Loading dataset...
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    if (!dataset) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0a0e1a] via-[#1a1f2e] to-[#2a2f3e]">
                <Navbar />
                <main className="pt-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="text-center py-12">
                            <div className="text-white/60 text-lg mb-4">Dataset not found</div>
                            <button
                                onClick={() => navigate('/rekognition-management')}
                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-sm font-medium transition-all duration-200"
                            >
                                Back to Datasets
                            </button>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <>
        <div className="min-h-screen bg-gradient-to-br from-[#0a0e1a] via-[#1a1f2e] to-[#2a2f3e]">
            <Navbar />
            <main className="pt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <button
                                onClick={() => navigate('/rekognition-management')}
                                className="text-[#4cc3e9] hover:text-white text-sm mb-2 flex items-center gap-2"
                            >
                                ← Back to Datasets
                            </button>
                            <h1 className="text-3xl font-bold text-white">{dataset.name}</h1>
                            <div className="flex items-center gap-6 mt-2 text-sm text-white/60">
                                <span>Images: {dataset.images_count || 0}</span>
                                <span>Labels: {dataset.labels_count || 0}</span>
                                <span>Annotations: {dataset.annotations_count || 0}</span>
                            </div>
                        </div>
                        
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="mb-6 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                            {error}
                            <button onClick={() => setError('')} className="ml-2 text-red-300 hover:text-white">×</button>
                        </div>
                    )}

                    {/* Main Layout with Sidebar */}
                    <div className="flex gap-6 h-[calc(100vh-12rem)]">
                        {/* Left Sidebar - Labels */}
                        <div className="w-80 bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-4 border border-[#4cc3e9]/30">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-white">Labels ({labels.length})</h2>
                                <button
                                    onClick={() => setShowLabelModal(true)}
                                    className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-sm font-medium transition-all duration-200"
                                >
                                    + Add
                                </button>
                            </div>

                            {selectedLabelFilterId && (
                                <div className="flex items-center justify-between mb-3 text-xs text-white/80 bg-white/10 border border-white/20 rounded-lg px-2 py-1">
                                    <span>
                                        Filtering by: {labels.find(l => l.id === selectedLabelFilterId)?.name || 'Label'}
                                    </span>
                                    <button onClick={clearLabelFilter} className="text-white/70 hover:text-white">Clear</button>
                                </div>
                            )}

                            <div className="space-y-2 overflow-y-auto max-h-[calc(100%-4rem)]">
                                {labels.length === 0 ? (
                                    <div className="text-center py-8">
                                        <div className="text-white/60 text-sm mb-3">No labels yet</div>
                                        <button
                                            onClick={() => setShowLabelModal(true)}
                                            className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-sm font-medium transition-all duration-200"
                                        >
                                            Create First Label
                                        </button>
                                    </div>
                                ) : (
                                    labels.map((label, index) => (
                                        <div
                                            key={label.id}
                                            className={`flex items-center gap-3 p-3 rounded-lg border transition-colors group cursor-pointer ${
                                                selectedLabelFilterId === label.id
                                                    ? 'bg-white/20 border-[#4cc3e9]/60'
                                                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                                            }`}
                                            onClick={() => handleLabelFilterClick(label.id)}
                                            title="Click to filter images by this label"
                                        >
                                            <div
                                                className="w-4 h-4 rounded flex-shrink-0"
                                                style={{ backgroundColor: `hsl(${(index * 60) % 360}, 70%, 50%)` }}
                                            ></div>
                                            <span className="text-white font-medium text-sm truncate flex-1">{label.name}</span>
                                            <span className="px-2 py-0.5 rounded bg-white/10 border border-white/20 text-white/80 text-xs flex-shrink-0" title="Annotations count">
                                                {typeof label.annotations_count === 'number' ? label.annotations_count : 0}
                                            </span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showDeleteConfirmation(label);
                                                }}
                                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 text-xs p-1 transition-all duration-200 flex-shrink-0"
                                                title="Delete label"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Main Content - Images */}
                        <div className="flex-1 flex flex-col">
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                                <div className="flex items-center gap-4">
                                    <h2 className="text-xl font-semibold text-white">Images ({filteredImages.length})</h2>
                                    {filteredImages.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedVisibleCount === filteredImages.length && filteredImages.length > 0}
                                                onChange={toggleAllImages}
                                                className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm text-white/70">
                                                Select All ({selectedVisibleCount}/{filteredImages.length})
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <div className="flex items-center bg-white/10 border border-white/20 rounded-lg overflow-hidden text-white text-xs">
                                        <button onClick={() => setImageFilter('all')} className={`px-3 py-1.5 ${imageFilter === 'all' ? 'bg-white/20' : 'hover:bg-white/10'}`}>All</button>
                                        <button onClick={() => setImageFilter('annotated')} className={`px-3 py-1.5 ${imageFilter === 'annotated' ? 'bg-white/20' : 'hover:bg-white/10'}`}>Annotated</button>
                                        <button onClick={() => setImageFilter('unannotated')} className={`px-3 py-1.5 ${imageFilter === 'unannotated' ? 'bg-white/20' : 'hover:bg-white/10'}`}>Unannotated</button>
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (selectedImages.size > 0) setShowDeleteSelectedModal(true);
                                            else setShowClearDatasetModal(true);
                                        }}
                                        disabled={clearing || deletingSelected}
                                        className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white text-sm font-medium transition-all duration-200 flex items-center gap-2"
                                        title={selectedImages.size > 0 ? 'Delete only the selected images' : 'Remove all images, labels, and annotations from this dataset'}
                                    >
                                        {(clearing || deletingSelected) ? (
                                            <span className="flex items-center gap-2">
                                                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                                </svg>
                                                {deletingSelected ? 'Deleting...' : 'Clearing...'}
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-2">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7-2h8a1 1 0 011 1v2H7V6a1 1 0 011-1z" />
                                                </svg>
                                                {selectedImages.size > 0 ? `Delete Selected (${selectedImages.size})` : 'Clear Dataset'}
                                            </span>
                                        )}
                                    </button>
                                    {selectedImages.size > 0 && (
                                        <button
                                            onClick={() => setShowBulkAutolabelModal(true)}
                                            disabled={Object.values(autolabelLoading).some(loading => loading)}
                                            className="px-3 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-600 disabled:from-blue-600/50 disabled:to-blue-700/50 text-white text-sm font-medium transition-all duration-200 flex items-center gap-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            Auto-Label Selected ({selectedImages.size})
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setShowImageModal(true)}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-sm font-medium transition-all duration-200"
                                    >
                                        + Add Image
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto">
                                {filteredImages.length === 0 ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                            <div className="text-white/60 text-lg mb-4">No images match the current filter</div>
                                            <button
                                                onClick={() => setShowImageModal(true)}
                                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-sm font-medium transition-all duration-200"
                                            >
                                                Add Your First Image
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-1">
                                        {filteredImages.map((image) => (
                                            <div
                                                key={image.id}
                                                className={`bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl overflow-hidden border transition-all duration-200 cursor-pointer group ${
                                                    selectedImages.has(image.id) 
                                                        ? 'border-blue-500 ring-2 ring-blue-500/50' 
                                                        : 'border-[#4cc3e9]/30 hover:border-[#4cc3e9]/50'
                                                }`}
                                                onClick={(e) => {
                                                    // Don't open modal if clicking on checkbox area
                                                    if (e.target.type !== 'checkbox') {
                                                        setSelectedImage(image);
                                                        fetchAnnotations(image.id);
                                                    }
                                                }}
                                            >
                                                <div className="aspect-square bg-gray-800 flex items-center justify-center overflow-hidden relative">
                                                    <img
                                                        src={getImageUrl(image)}
                                                        alt={`Image ${image.id}`}
                                                        className="w-full h-full object-contain bg-black"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div className="w-full h-full flex items-center justify-center text-white/60 text-sm" style={{display: 'none'}}>
                                                        Image not available
                                                    </div>
                                                    {/* Annotation overlay on card */}
                                                    <div className="absolute inset-0 pointer-events-none">
                                                        {(imageAnnotationsMap[image.id] || []).map((ann, idx) => {
                                                            const l = (ann.left_px / Math.max(1, image.width_px)) * 100;
                                                            const t = (ann.top_px / Math.max(1, image.height_px)) * 100;
                                                            const w = (ann.width_px / Math.max(1, image.width_px)) * 100;
                                                            const h = (ann.height_px / Math.max(1, image.height_px)) * 100;
                                                            return (
                                                                <div
                                                                    key={ann.id || idx}
                                                                    style={{
                                                                        position: 'absolute',
                                                                        left: `${l}%`,
                                                                        top: `${t}%`,
                                                                        width: `${w}%`,
                                                                        height: `${h}%`,
                                                                        border: '2px solid rgba(76,195,233,0.9)',
                                                                        boxShadow: '0 0 0 1px rgba(0,0,0,0.3) inset',
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                        {/* Pending autolabel overlays (if any) */}
                                                        {!!pendingAutolabels[image.id] && (pendingAutolabels[image.id].annotations || []).map((ann, idx) => {
                                                            const l = (ann.left_px / Math.max(1, image.width_px)) * 100;
                                                            const t = (ann.top_px / Math.max(1, image.height_px)) * 100;
                                                            const w = (ann.width_px / Math.max(1, image.width_px)) * 100;
                                                            const h = (ann.height_px / Math.max(1, image.height_px)) * 100;
                                                            return (
                                                                <div
                                                                    key={`p-${idx}`}
                                                                    style={{
                                                                        position: 'absolute',
                                                                        left: `${l}%`,
                                                                        top: `${t}%`,
                                                                        width: `${w}%`,
                                                                        height: `${h}%`,
                                                                        border: '2px dashed #3b82f6',
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                    {/* Selection checkbox */}
                                                    <div 
                                                        className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-all duration-200 p-1 bg-black/50 rounded cursor-pointer z-10"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleImageSelection(image.id);
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedImages.has(image.id)}
                                                            onChange={() => {}} // Handled by parent div click
                                                            className="w-5 h-5 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 pointer-events-none"
                                                        />
                                                    </div>

                                                    {/* Delete button overlay */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            showDeleteImageConfirmation(image);
                                                        }}
                                                        className="absolute top-2 right-2 w-8 h-8 bg-red-600/80 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
                                                        title="Delete image"
                                                    >
                                                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                                <div className="p-3">
                                                    <div className="text-sm font-medium text-white truncate">
                                                        {image.s3_key.split('/').pop()}
                                                    </div>
                                                    <div className="text-xs text-white/60 mt-1">
                                                        {image.width_px} × {image.height_px}
                                                    </div>
                                                    <div className="text-xs text-white/60 mt-1">
                                                        Annotations: {image.annotations_count || 0}
                                                        {pendingAutolabels[image.id] && (
                                                            <span className="text-blue-400 ml-1">
                                                                (+{pendingAutolabels[image.id].annotations.length} pending)
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pagination Controls */}
                    {!imagesLoading && images.length > 0 && paginationMeta.totalPages > 1 && (
                        <div className="mt-8 flex items-center justify-between">
                            <div className="text-sm text-white/60">
                                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} images
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={!paginationMeta.hasPrevious || imagesLoading}
                                    className="px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                
                                {/* Page numbers */}
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(paginationMeta.totalPages, 5) }, (_, i) => {
                                        let page;
                                        if (paginationMeta.totalPages <= 5) {
                                            page = i + 1;
                                        } else if (currentPage <= 3) {
                                            page = i + 1;
                                        } else if (currentPage >= paginationMeta.totalPages - 2) {
                                            page = paginationMeta.totalPages - 4 + i;
                                        } else {
                                            page = currentPage - 2 + i;
                                        }
                                        
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                disabled={imagesLoading}
                                                className={`px-3 py-2 rounded-lg transition-colors ${
                                                    page === currentPage
                                                        ? 'bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] text-white'
                                                        : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                </div>
                                
                                <button
                                    onClick={handleNextPage}
                                    disabled={!paginationMeta.hasNext || imagesLoading}
                                    className="px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Image Annotation Modal */}
                    <ImageAnnotationModal
                        selectedImage={selectedImage}
                        onClose={() => setSelectedImage(null)}
                        annotations={annotations}
                        labels={labels}
                        canvasRef={canvasRef}
                        imageRef={imageRef}
                        isAnnotating={isAnnotating}
                        selectedLabel={selectedLabel}
                        setIsAnnotating={setIsAnnotating}
                        setSelectedLabel={setSelectedLabel}
                        currentImageAutolabelSettings={currentImageAutolabelSettings}
                        setCurrentImageAutolabelSettings={setCurrentImageAutolabelSettings}
                        pendingAutolabels={pendingAutolabels}
                        autolabelLoading={autolabelLoading}
                        autolabelImage={autolabelImage}
                        saveAutolabels={saveAutolabels}
                        discardAutolabels={discardAutolabels}
                        currentIndex={currentIndex}
                        gotoPrevImage={gotoPrevImage}
                        gotoNextImage={gotoNextImage}
                        filteredImages={filteredImages}
                        images={images}
                        handleMouseDown={handleMouseDown}
                        handleMouseMove={handleMouseMove}
                        handleMouseUp={handleMouseUp}
                        drawAnnotations={drawAnnotations}
                        getImageUrl={getImageUrl}
                        deleteAnnotation={deleteAnnotation}
                        relabelAnnotation={relabelAnnotation}
                    />

                    {/* Create Label Modal */}
                    {showLabelModal && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 p-6">
                                <h2 className="text-xl font-semibold text-white mb-4">Create New Label</h2>
                                
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-white/80 mb-2">
                                        Label Name
                                    </label>
                                    <input
                                        type="text"
                                        value={newLabelName}
                                        onChange={(e) => setNewLabelName(e.target.value)}
                                        placeholder="Enter label name..."
                                        className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                createLabel();
                                            }
                                        }}
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowLabelModal(false);
                                            setNewLabelName('');
                                        }}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={createLabel}
                                        disabled={!newLabelName.trim()}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Create Label
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Bulk Autolabel Modal */}
                    {showBulkAutolabelModal && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 p-6">
                                <h2 className="text-xl font-semibold text-white mb-4">
                                    Auto-Label Selected Images ({selectedImages.size})
                                </h2>
                                
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-white/80 mb-2">
                                            Model Type
                                        </label>
                                        <select
                                            value={bulkAutolabelSettings.type}
                                            onChange={(e) => setBulkAutolabelSettings(prev => ({ ...prev, type: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white [&>option]:bg-gray-800 [&>option]:text-white"
                                        >
                                            <option value="series" className="bg-gray-800 text-white">Series</option>
                                            <option value="matches" className="bg-gray-800 text-white">Matches</option>
                                        </select>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-white/80 mb-2">
                                            Minimum Confidence (%)
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={bulkAutolabelSettings.minConfidence}
                                            onChange={(e) => setBulkAutolabelSettings(prev => ({ ...prev, minConfidence: Number(e.target.value) }))}
                                            className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white"
                                            placeholder="80"
                                        />
                                    </div>
                                    
                                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                                        <div className="text-yellow-300 text-sm">
                                            ⚠️ This will generate auto-labels for {selectedImages.size} selected images. You can review and save each image's results individually.
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setShowBulkAutolabelModal(false)}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={bulkAutolabelSelectedImages}
                                        disabled={Object.values(autolabelLoading).some(loading => loading)}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-600 disabled:from-blue-600/50 disabled:to-blue-700/50 text-white font-medium transition-all duration-200 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                        Start Auto-Labeling
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Add Image Modal */}
                    {showImageModal && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 p-6">
                                <h2 className="text-xl font-semibold text-white mb-4">Add New Image</h2>
                                
                                {/* Upload Mode Selector */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-white/80 mb-2">
                                        Upload Method
                                    </label>
                                    <div className="flex items-center gap-4">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                value="file"
                                                checked={uploadMode === 'file'}
                                                onChange={(e) => setUploadMode(e.target.value)}
                                                className="text-[#4cc3e9]"
                                            />
                                            <span className="text-white text-sm">Upload File</span>
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                value="s3"
                                                checked={uploadMode === 's3'}
                                                onChange={(e) => setUploadMode(e.target.value)}
                                                className="text-[#4cc3e9]"
                                            />
                                            <span className="text-white text-sm">S3 Reference</span>
                                        </label>
                                    </div>
                                </div>

                                {uploadMode === 'file' ? (
                                    // File Upload Mode
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-white/80 mb-2">
                                                Select Image File
                                            </label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileSelect}
                                                className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-[#4cc3e9] file:text-white hover:file:bg-[#4cc3e9]/80"
                                            />
                                        </div>
                                        
                                        {selectedFile && (
                                            <div className="text-sm text-white/70">
                                                Selected: {selectedFile.name}
                                            </div>
                                        )}

                                        {/* Optional dimension override */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-white/60 mb-1">
                                                    Width (px) - Optional
                                                </label>
                                                <input
                                                    type="number"
                                                    value={newImageData.width_px}
                                                    onChange={(e) => setNewImageData(prev => ({...prev, width_px: e.target.value}))}
                                                    placeholder="Auto-detect"
                                                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-white/60 mb-1">
                                                    Height (px) - Optional
                                                </label>
                                                <input
                                                    type="number"
                                                    value={newImageData.height_px}
                                                    onChange={(e) => setNewImageData(prev => ({...prev, height_px: e.target.value}))}
                                                    placeholder="Auto-detect"
                                                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // S3 Reference Mode
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-white/80 mb-2">
                                                S3 Bucket
                                            </label>
                                            <input
                                                type="text"
                                                value={newImageData.s3_bucket}
                                                onChange={(e) => setNewImageData(prev => ({...prev, s3_bucket: e.target.value}))}
                                                placeholder="my-bucket-name"
                                                className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-white/80 mb-2">
                                                S3 Key
                                            </label>
                                            <input
                                                type="text"
                                                value={newImageData.s3_key}
                                                onChange={(e) => setNewImageData(prev => ({...prev, s3_key: e.target.value}))}
                                                placeholder="path/to/image.jpg"
                                                className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-white/80 mb-2">
                                                    Width (px)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={newImageData.width_px}
                                                    onChange={(e) => setNewImageData(prev => ({...prev, width_px: e.target.value}))}
                                                    placeholder="1920"
                                                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-white/80 mb-2">
                                                    Height (px)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={newImageData.height_px}
                                                    onChange={(e) => setNewImageData(prev => ({...prev, height_px: e.target.value}))}
                                                    placeholder="1080"
                                                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {error && (
                                    <div className="mt-4 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded p-2">
                                        {error}
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowImageModal(false);
                                            setSelectedFile(null);
                                            setNewImageData({
                                                s3_bucket: '',
                                                s3_key: '',
                                                width_px: '',
                                                height_px: ''
                                            });
                                            setError('');
                                        }}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                        disabled={uploading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={addImage}
                                        disabled={uploading || (uploadMode === 'file' && !selectedFile) || (uploadMode === 's3' && (!newImageData.s3_bucket.trim() || !newImageData.s3_key.trim() || !newImageData.width_px || !newImageData.height_px))}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {uploading ? 'Adding...' : 'Add Image'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Confirmation Modal */}
                    {showDeleteModal && labelToDelete && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-red-500/30 p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-white">Delete Label</h2>
                                        <p className="text-sm text-white/60">This action cannot be undone</p>
                                    </div>
                                </div>
                                
                                <div className="mb-6">
                                    <p className="text-white/90 mb-3">
                                        Are you sure you want to delete the label <span className="font-semibold text-white">"{labelToDelete.name}"</span>?
                                    </p>
                                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                            <div className="text-sm text-red-300">
                                                <div className="font-medium mb-1">Warning</div>
                                                <div>This will also permanently delete all annotations that use this label across all images in the dataset.</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowDeleteModal(false);
                                            setLabelToDelete(null);
                                        }}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={deleteLabel}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium transition-all duration-200"
                                    >
                                        Delete Label
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Image Confirmation Modal */}
                    {showDeleteImageModal && imageToDelete && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-red-500/30 p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-white">Delete Image</h2>
                                        <p className="text-sm text-white/60">This action cannot be undone</p>
                                    </div>
                                </div>
                                
                                <div className="mb-6">
                                    <p className="text-white/90 mb-3">
                                        Are you sure you want to delete this image <span className="font-semibold text-white">"{imageToDelete.s3_key.split('/').pop()}"</span>?
                                    </p>
                                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                            <div className="text-sm text-red-300">
                                                <div className="font-medium mb-1">Warning</div>
                                                <div>This will permanently delete the image and all of its annotations from the dataset.</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowDeleteImageModal(false);
                                            setImageToDelete(null);
                                        }}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={deleteImage}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium transition-all duration-200"
                                    >
                                        Delete Image
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Clear Dataset Confirmation Modal */}
                    {showClearDatasetModal && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-red-500/30 p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-white">Clear Dataset</h2>
                                        <p className="text-sm text-white/60">This action cannot be undone</p>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <p className="text-white/90 mb-3">
                                        Are you sure you want to clear this dataset? This will remove all images, labels, and annotations.
                                    </p>
                                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                            <div className="text-sm text-red-300">
                                                <div className="font-medium mb-1">Warning</div>
                                                <div>This will permanently delete all content in the dataset.</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowClearDatasetModal(false)}
                                        disabled={clearing}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={clearDataset}
                                        disabled={clearing}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium transition-all duration-200"
                                    >
                                        {clearing ? 'Clearing...' : 'Clear Dataset'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Selected Images Confirmation Modal */}
                    {showDeleteSelectedModal && selectedImages.size > 0 && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-red-500/30 p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-white">Delete Selected Images</h2>
                                        <p className="text-sm text-white/60">This action cannot be undone</p>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <p className="text-white/90 mb-3">
                                        Are you sure you want to delete {selectedImages.size} selected image{selectedImages.size > 1 ? 's' : ''}? This will permanently delete the images and all of their annotations.
                                    </p>
                                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                            <div className="text-sm text-red-300">
                                                <div className="font-medium mb-1">Warning</div>
                                                <div>This will permanently delete the selected images and their annotations.</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowDeleteSelectedModal(false)}
                                        disabled={deletingSelected}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={deleteSelectedImages}
                                        disabled={deletingSelected}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium transition-all duration-200"
                                    >
                                        {deletingSelected ? 'Deleting...' : 'Delete Selected'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Relabel Annotation Modal */}
                    {showRelabelModal && annotationToRelabel && (
                        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 p-6">
                                <h2 className="text-lg font-semibold text-white mb-4">Relabel Annotation</h2>
                                
                                <div className="mb-4">
                                    <p className="text-sm text-white/70 mb-3">
                                        Current label: <span className="text-white font-medium">{labels.find(l => l.id === annotationToRelabel.label_id)?.name || 'Unknown'}</span>
                                    </p>
                                    <label className="block text-sm font-medium text-white/80 mb-2">
                                        New Label
                                    </label>
                                    <select
                                        value={relabelTargetLabelId}
                                        onChange={(e) => setRelabelTargetLabelId(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white [&>option]:bg-gray-800 [&>option]:text-white"
                                    >
                                        <option value="" className="bg-gray-800 text-white">Select new label...</option>
                                        {labels
                                            .filter(l => l.id !== annotationToRelabel.label_id)
                                            .map(label => (
                                                <option key={label.id} value={label.id} className="bg-gray-800 text-white">{label.name}</option>
                                            ))
                                        }
                                    </select>
                                </div>

                                {error && (
                                    <div className="mb-4 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded p-2">
                                        {error}
                                    </div>
                                )}

                                <div className="flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowRelabelModal(false);
                                            setAnnotationToRelabel(null);
                                            setRelabelTargetLabelId('');
                                            setError('');
                                        }}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-colors"
                                        disabled={relabeling}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={executeRelabel}
                                        disabled={relabeling || !relabelTargetLabelId}
                                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {relabeling ? 'Relabeling...' : 'Update Label'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
        </>
    );
}

export default DatasetDetail;
